import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import { ChevronUpIcon, ChevronDownIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { GET_USERS_ADMIN } from '../../graphql/queries';
import { Spinner } from '../common/Spinner';
import UserDetailModal from './UserDetailModal';

const PAGE_SIZE = 50;

interface AdminUserRow {
  userId: string;
  email: string;
  name?: string | null;
  userType?: string | null;
  status?: string | null;
  oauthProvider?: string | null;
  hasSocialLinked?: boolean | null;
  createdAt?: string | null;
}

interface AdminUserStats {
  total: number;
  influencer: number;
  brand: number;
  agency: number;
  dailyNew: number;
  monthlyNew: number;
}

interface AdminUsersResult {
  users: AdminUserRow[];
  count: number;
  total: number;
  page: number;
  pageSize: number;
  stats: AdminUserStats;
}

type SortKey = 'name' | 'email' | 'userType' | 'status' | 'createdAt' | 'hasSocialLinked';
type SortDir = 'asc' | 'desc';

const formatDate = (iso?: string | null) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '-';
  return d.toISOString().slice(0, 10);
};

const userTypeBadgeClass = (ut?: string | null) => {
  switch (ut) {
    case 'influencer':
      return 'bg-purple-100 text-purple-700';
    case 'brand':
      return 'bg-blue-100 text-blue-700';
    case 'agency':
      return 'bg-teal-100 text-teal-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

const statusBadgeClass = (s?: string | null) => {
  switch (s) {
    case 'active':
      return 'bg-green-100 text-green-700';
    case 'suspended':
      return 'bg-red-100 text-red-700';
    case 'pending':
      return 'bg-yellow-100 text-yellow-700';
    case 'withdrawn':
      return 'bg-gray-200 text-gray-500';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

/**
 * Compare two AdminUserRow values for sorting. Nullish values sort last
 * regardless of direction so empty cells don't noisy up the top of the list.
 */
function compareRows(a: AdminUserRow, b: AdminUserRow, key: SortKey, dir: SortDir): number {
  const mult = dir === 'asc' ? 1 : -1;

  const pick = (r: AdminUserRow): string | number | boolean | null | undefined => {
    switch (key) {
      case 'name': return r.name ?? '';
      case 'email': return r.email ?? '';
      case 'userType': return r.userType ?? '';
      case 'status': return r.status ?? 'active';
      case 'createdAt': return r.createdAt ?? '';
      case 'hasSocialLinked': return r.hasSocialLinked ?? false;
    }
  };

  const av = pick(a);
  const bv = pick(b);

  // Push empty strings to the bottom regardless of direction.
  const aEmpty = av === '' || av === null || av === undefined;
  const bEmpty = bv === '' || bv === null || bv === undefined;
  if (aEmpty && !bEmpty) return 1;
  if (!aEmpty && bEmpty) return -1;
  if (aEmpty && bEmpty) return 0;

  if (typeof av === 'boolean' && typeof bv === 'boolean') {
    return (Number(av) - Number(bv)) * mult;
  }
  if (typeof av === 'number' && typeof bv === 'number') {
    return (av - bv) * mult;
  }
  return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * mult;
}

const UserManagementPage: React.FC = () => {
  const { t } = useTranslation(['admin', 'common', 'profile']);
  const navigate = useNavigate();

  const [userType, setUserType] = useState('');
  const [status, setStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Default sort: newest first.
  const [sortKey, setSortKey] = useState<SortKey | null>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const [detailUserId, setDetailUserId] = useState<string | null>(null);

  const handleSort = (key: SortKey) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('asc');
      return;
    }
    // Cycle: asc → desc → cleared
    if (sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortKey(null);
    }
  };

  // Debounce search input so we don't refetch on every keystroke.
  useEffect(() => {
    const id = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  // Reset page when filters change.
  useEffect(() => {
    setPage(1);
  }, [userType, status]);

  const { data, loading, error } = useQuery<{ getUsersAdmin: AdminUsersResult }>(
    gql`${GET_USERS_ADMIN}`,
    {
      variables: {
        filters: {
          userType: userType || null,
          status: status || null,
          search: search || null,
          page,
          pageSize: PAGE_SIZE,
        },
      },
      fetchPolicy: 'network-only',
    },
  );

  const result = data?.getUsersAdmin;
  const stats = result?.stats;
  const rawUsers = result?.users ?? [];
  const total = result?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Client-side sort over the current page only. When the user clears sort,
  // fall back to the backend's default ordering (newest first).
  const users = useMemo(() => {
    if (!sortKey) return rawUsers;
    return [...rawUsers].sort((a, b) => compareRows(a, b, sortKey, sortDir));
  }, [rawUsers, sortKey, sortDir]);

  const pageNumbers = useMemo(() => {
    const window = 3;
    const from = Math.max(1, page - window);
    const to = Math.min(pageCount, page + window);
    const arr: number[] = [];
    for (let p = from; p <= to; p++) arr.push(p);
    return arr;
  }, [page, pageCount]);

  const SortIcon: React.FC<{ columnKey: SortKey }> = ({ columnKey }) => {
    if (sortKey !== columnKey) {
      return <ChevronUpDownIcon className="w-3.5 h-3.5 text-gray-300 inline-block" />;
    }
    return sortDir === 'asc' ? (
      <ChevronUpIcon className="w-3.5 h-3.5 text-blue-600 inline-block" />
    ) : (
      <ChevronDownIcon className="w-3.5 h-3.5 text-blue-600 inline-block" />
    );
  };

  const headerCell = (columnKey: SortKey, labelKey: string) => (
    <th className="px-4 py-3 text-left font-medium">
      <button
        type="button"
        onClick={() => handleSort(columnKey)}
        className={`inline-flex items-center gap-1 hover:text-gray-900 transition-colors ${
          sortKey === columnKey ? 'text-gray-900' : 'text-gray-600'
        }`}
      >
        <span>{t(labelKey)}</span>
        <SortIcon columnKey={columnKey} />
      </button>
    </th>
  );

  return (
    <div className="min-h-full bg-gray-50">
      {/* Breadcrumb */}
      <div className="max-w-[1200px] mx-auto px-4 py-3">
        <nav className="text-sm text-gray-500">
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="hover:text-blue-600 transition-colors"
          >
            {t('common:profileDropdown.adminDashboard')}
          </button>
          <span className="mx-1.5">&gt;</span>
          <span>{t('admin:sidebar.accountManagement')}</span>
          <span className="mx-1.5">&gt;</span>
          <span className="text-gray-900 font-medium">{t('admin:sidebar.users')}</span>
        </nav>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 pb-16 space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('admin:sidebar.users')}</h1>

        {/* Stats */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left font-medium">{t('admin:users.stats.total')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('admin:users.stats.influencer')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('admin:users.stats.brand')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('admin:users.stats.agency')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('admin:users.stats.dailyNew')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('admin:users.stats.monthlyNew')}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-gray-100">
                <td className="px-4 py-3 text-lg font-bold text-gray-900">
                  {loading ? '—' : t('admin:users.count', { count: stats?.total ?? 0 })}
                </td>
                <td className="px-4 py-3 text-lg font-bold text-gray-900">
                  {loading ? '—' : t('admin:users.count', { count: stats?.influencer ?? 0 })}
                </td>
                <td className="px-4 py-3 text-lg font-bold text-gray-900">
                  {loading ? '—' : t('admin:users.count', { count: stats?.brand ?? 0 })}
                </td>
                <td className="px-4 py-3 text-lg font-bold text-gray-900">
                  {loading ? '—' : t('admin:users.count', { count: stats?.agency ?? 0 })}
                </td>
                <td className="px-4 py-3 text-lg font-bold text-gray-900">
                  {loading ? '—' : t('admin:users.count', { count: stats?.dailyNew ?? 0 })}
                </td>
                <td className="px-4 py-3 text-lg font-bold text-gray-900">
                  {loading ? '—' : t('admin:users.count', { count: stats?.monthlyNew ?? 0 })}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-wrap gap-3 items-center">
          <select
            value={userType}
            onChange={(e) => setUserType(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500"
          >
            <option value="">{t('admin:users.filter.allUserTypes')}</option>
            <option value="influencer">{t('admin:users.types.influencer')}</option>
            <option value="brand">{t('admin:users.types.brand')}</option>
            <option value="agency">{t('admin:users.types.agency')}</option>
          </select>

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500"
          >
            <option value="">{t('admin:users.filter.allStatuses')}</option>
            <option value="active">{t('admin:users.status.active')}</option>
            <option value="suspended">{t('admin:users.status.suspended')}</option>
            <option value="pending">{t('admin:users.status.pending')}</option>
          </select>

          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('admin:users.filter.searchPlaceholder') as string}
            className="flex-1 min-w-[220px] px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {error && (
            <div className="px-4 py-6 text-sm text-red-600">
              {error.message}
            </div>
          )}

          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                {headerCell('name', 'admin:users.table.name')}
                {headerCell('email', 'admin:users.table.email')}
                {headerCell('userType', 'admin:users.table.userType')}
                {headerCell('status', 'admin:users.table.status')}
                {headerCell('createdAt', 'admin:users.table.joinedAt')}
                {headerCell('hasSocialLinked', 'admin:users.table.socialLinked')}
                <th className="px-4 py-3 text-left font-medium">{t('admin:users.table.action')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center">
                    <Spinner size="medium" />
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                    {t('admin:users.empty')}
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.userId} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-900 font-medium">{u.name || '-'}</td>
                    <td className="px-4 py-3 text-gray-700">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${userTypeBadgeClass(u.userType)}`}>
                        {u.userType ? t(`admin:users.types.${u.userType}`, u.userType) : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(u.status)}`}>
                        {t(`admin:users.status.${u.status || 'active'}`, u.status || 'active')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(u.createdAt)}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {u.hasSocialLinked
                        ? (u.oauthProvider || t('admin:users.social.linked'))
                        : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => setDetailUserId(u.userId)}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        {t('admin:users.table.detail')}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && total > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {t('admin:users.totalCount', { count: total })}
            </span>

            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
              >
                {t('admin:users.pagination.prev')}
              </button>

              {pageNumbers[0] > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => setPage(1)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    1
                  </button>
                  {pageNumbers[0] > 2 && <span className="px-1 text-gray-400">…</span>}
                </>
              )}

              {pageNumbers.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p)}
                  className={`px-3 py-1.5 text-sm border rounded-md ${
                    p === page
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {p}
                </button>
              ))}

              {pageNumbers[pageNumbers.length - 1] < pageCount && (
                <>
                  {pageNumbers[pageNumbers.length - 1] < pageCount - 1 && (
                    <span className="px-1 text-gray-400">…</span>
                  )}
                  <button
                    type="button"
                    onClick={() => setPage(pageCount)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    {pageCount}
                  </button>
                </>
              )}

              <button
                type="button"
                disabled={page >= pageCount}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
              >
                {t('admin:users.pagination.next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {detailUserId && (
        <UserDetailModal userId={detailUserId} onClose={() => setDetailUserId(null)} />
      )}
    </div>
  );
};

export default UserManagementPage;
